import { useQuery } from '@apollo/client';
import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  TitleText,
  AllSetContainer,
  FinishedSubtitle,
  Link
} from '../Components/Comprehension/Comprehension.styled';
import Button from 'components/Buttons/NewButton';
import { utils } from 'kb-shared';
import { BugTracker } from 'kb-shared/utilities/bugTracker';
import { STATUS } from 'screens/InterstitialModal/InterstitialModal.graphql';
import { InterstitialStatusResponse } from 'screens/InterstitialModal/InterstitialModal.types';
import { filterConsentsWithLinkUrl, getConsentsWithoutLinkUrl } from 'utilities/consent';
import { pageUrl } from 'utilities/pageUrl';

import { getReturnBackRoute } from '../ConsentStateManager.utils';

export const FinalStep = ({ consentId }: { consentId: number }) => {
  const navigate = useNavigate();
  const response = useQuery<InterstitialStatusResponse>(STATUS);
  const loggedInvalidConsents = useRef<string>('');
  const requiredConsentsFromResponse = response.data?.interstitialStatus?.requiredConsents || [];
  const consentsWithoutLinkUrl = getConsentsWithoutLinkUrl(requiredConsentsFromResponse);
  const invalidConsentIds = consentsWithoutLinkUrl.map(consent => consent.id).join(',');

  useEffect(() => {
    if (!invalidConsentIds || loggedInvalidConsents.current === invalidConsentIds) return;

    loggedInvalidConsents.current = invalidConsentIds;
    BugTracker.notify(
      new Error(`Invalid final step required consents without linkUrl: ${invalidConsentIds}`),
      'InvalidFinalStepRequiredConsents'
    );
  }, [invalidConsentIds]);

  // do not show consent at all if they do not have an outstanding consent to complete
  const requiredConsents = filterConsentsWithLinkUrl(requiredConsentsFromResponse);
  const consentsIds = requiredConsents.map((consent): number =>
    parseInt(consent.linkUrl.replace(`${pageUrl.consents()}/`, ''))
  );
  const isRequiredConsent = consentsIds?.includes(consentId);

  const title = isRequiredConsent ? 'Thank you for signing this consent!' : `You're all set!`;

  const { text, pathname } = getReturnBackRoute();

  const buttonText = isRequiredConsent ? 'Continue account setup' : text;

  return (
    <AllSetContainer>
      <TitleText>{title}</TitleText>
      <FinishedSubtitle>
        If you have any questions, contact your care team at{' '}
        <Link href={`mailto:${utils.KBContacts.navigatorEmail}?subject=Consent Forms`}>
          {utils.KBContacts.navigatorEmail}
        </Link>{' '}
        or <Link href={'tel://1-855-KND-BODY'}>1-855-KND-BODY</Link>
      </FinishedSubtitle>
      <Button text={buttonText} onClick={() => navigate(pathname)} />
    </AllSetContainer>
  );
};
